import { Notifications, UserProfile, UserProfileFormResource } from '@appTypes/header/UserProfile';
import Loader from '@components/shared/loader/Loader';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { CommonService } from '@services/common/CommonService';
import { UsersService } from '@services/users/UsersService';
import { ErrorToast } from '@services/utils/ToastUtils';
import { theme } from '@theme';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ModalBody } from '../../header/user-profile/UserProfileStyles';
import UserProfileForm from '../../header/user-profile/user-profile-form/UserProfileForm';
import UserProfileModalFooter from '../../header/user-profile/user-profile-modal/UserProfileModalFooter';
import { Typography } from '../../style/Typography';
import ConfirmationModal from '../modals/confirmation-modal/ConfirmationModal';

type UserProfileModalWrapperProps = {
  formData?: UserProfileFormResource;
  onClose: () => void;
  modalIsOpen: boolean;
  isBack?: boolean;
  setIsBack?: Dispatch<SetStateAction<boolean>>;
};

const UserProfileModalWrapper: FC<UserProfileModalWrapperProps> = (props) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showChangePassword, setShowChangePassword] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const [formData, setFormData] = useState<UserProfileFormResource>();
  const [originalData, setOriginalData] = useState<UserProfileFormResource>();
  const { isMobileDevice } = useCheckDevice();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(false);

  //bit flags
  const realtimeServiceArrivalFlag: number = 1;
  const serviceArrivalFlag: number = 2;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const parseBitFlag = (userProfile: UserProfile, emailPref: number): boolean => {
    return (userProfile.emailPreferences & emailPref) === emailPref;
  };

  const methods = useForm({
    defaultValues: formData,
    mode: 'onChange',
  });

  const { handleSubmit, reset } = methods;

  const onSubmit = (data: UserProfileFormResource): void => {
    setSubmitted(false);
    setFormData(data);
    setSubmitted(true);
  };

  const bodyComponent: JSX.Element = (
    <>
      <Typography size={theme.typography.h4}>
        If you exit without saving all your changes will be lost. Are you sure?
      </Typography>
    </>
  );

  const handleContinue = () => {
    props.setIsBack(false);
    setConfirmModalOpen(false);
  };

  const handleDiscard = () => {
    setPasswordValidation(true);
    setShowChangePassword(false);
    setConfirmModalOpen(false);
    props.setIsBack(false);
    setIsDirty(false);
    reset(originalData);
  };

  const handleConfirmationModal = () => {
    if (isDirty) {
      setConfirmModalOpen(true);
    } else {
      props.onClose();
    }
  };

  useEffect(() => {
    if (props.isBack) {
      handleConfirmationModal();
    }
  }, [props.isBack]);

  useEffect(() => {
    if (props.modalIsOpen) {
      setIsLoading(true);

      CommonService.getStores()
        .then((stores) => {
          const storeList = stores?.map((store) => ({
            storeId: store.storeCode,
            storeName: store.storeName,
          }));

          UsersService.getUserProfile()
            .then((res) => {
              const notifications: Notifications = res.notifications || {
                mobileNotificationsEnabled: false,
                preferences: [],
              };

              const userProfileForm: UserProfileFormResource = {
                confirmPassword: '',
                homePagePreference: res.homepagePreference.toString(),
                currentPassword: '',
                password: '',
                realtimeServiceArrivalEmail: parseBitFlag(res, realtimeServiceArrivalFlag),
                serviceArrivalEmail: parseBitFlag(res, serviceArrivalFlag),
                confirmPasswordDealer: '',
                newPasswordDealer: '',
                phoneNumber: res.phoneNumber,
                isOptedOutSms: res.isOptedOutSms,
                notifications,
              };

              const updatedNotificationPreferencesWithStoreName =
                userProfileForm?.notifications?.preferences.map((notification) => {
                  const store = storeList.find((store) => store.storeId === notification.storeId);
                  return {
                    ...notification,
                    storeName: store?.storeName,
                  };
                });
              userProfileForm.notifications.preferences =
                updatedNotificationPreferencesWithStoreName;

              reset(userProfileForm);
              setFormData(userProfileForm);
              setOriginalData(userProfileForm);
            })
            .finally(() => {
              setIsLoading(false);
            });
        })
        .catch((err) => {
          ErrorToast('Error while fetching the Store List');
        });
    }
  }, [props.modalIsOpen]);
  return (
    <>
      {isLoading && <Loader padding='175px 0 0 0' />}
      {!isLoading && (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody>
              {formData && (
                <UserProfileForm
                  formData={formData}
                  showChangePassword={showChangePassword}
                  submitted={submitted}
                  onClose={props.onClose}
                  setIsSaving={setIsSaving}
                  setShowChangePassword={setShowChangePassword}
                  setSubmitted={setSubmitted}
                  setIsDirty={setIsDirty}
                  passwordValidation={passwordValidation}
                  setPasswordValidation={setPasswordValidation}
                />
              )}
            </ModalBody>
            <UserProfileModalFooter isSaving={isSaving} showChangePassword={showChangePassword} />
          </form>
        </FormProvider>
      )}
      {isMobileDevice && (
        <ConfirmationModal
          isOpen={confirmModalOpen}
          handleSubmitClick={handleContinue}
          handleCancelClick={handleDiscard}
          cancelButtonText='Discard Changes'
          submitButtonText='Continue Editing'
          title='Unsaved Changes'
          bodyComponent={bodyComponent}
          lineHeight='25px'
          buttonWidth='-webkit-fill-available'
          showCloseIcon={false}
          padding={'20px 50px 20px 20px'}
          minHeight={'225px'}
          minWidth='none'
        />
      )}
    </>
  );
};

export default UserProfileModalWrapper;
